//region header
//--------------------------------------------------------------------------------------------------
//
//  Description: Display the list of classifications for the property.
//
//  Copyright:    © 2023 - 2026 Idox Software Limited.
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier             Issue# Description
//region Version 1.0.0.0
//    001   18.09.23 Sean Flook                  Initial Revision.
//    002   06.10.23 Sean Flook                  Use colour variables.
//    003   27.10.23 Sean Flook                  Use new dataFormStyle.
//    004   24.11.23 Sean Flook                  Moved Box and Stack to @mui/system and fixed a warning.
//    005   08.12.23 Sean Flook                  Migrated DataGrid to v6.
//    006   05.01.24 Sean Flook                  Changes to sort out warnings and use CSS shortcuts.
//    007   16.01.24 Sean Flook                  Changes required to fix warnings.
//    008   25.01.24 Sean Flook                  Changes required after UX review.
//    009   16.02.24 Sean Flook         ESU16_GP If changing page etc ensure the information and selection controls are cleared.
//    010   20.02.24 Sean Flook         ESU16_GP Undone above change as not required.
//    011   11.03.24 Sean Flook            GLB12 Adjusted height to remove gap.
//    012   18.03.24 Sean Flook            GLB12 Adjusted height to remove overflow.
//    013   18.03.24 Sean Flook       STRFRM3_OS Set the styling for the header row of the data grid.
//    014   22.03.24 Sean Flook            GLB12 Changed to use dataFormStyle so height can be correctly set.
//    015   30.05.24 Joel Benford      IMANN-496 Add classification code avatar
//    016   20.06.24 Sean Flook        IMANN-636 Use the new user rights.
//endregion Version 1.0.0.0
//region Version 1.0.1.0
//    017   31.10.24 Sean Flook       IMANN-1012 Changed height of skeleton control.
//endregion Version 1.0.1.0
//region Version 1.0.5.0
//    018   27.01.25 Sean Flook       IMANN-1077 Upgraded MUI to v6.
//    019   14.03.25 Sean Flook        IMANN-963 Prevent the selection control from displaying if the user cannot edit the data.
//    020   23.04.25 Sean Flook       IMANN-1768 Changes required for selecting properties in property and street forms.
//    021   24.04.25 Sean Flook       IMANN-1760 Prevent multiple renders from occurring.
//endregion Version 1.0.5.0
//region Version 1.0.6.0
//    022   04.07.25 Sean Flook                  Set the new variant parameter for the ADSSelectionControl.
//    023   08.08.25 Sean Flook       IMANN-1960 Moved styling for DataGrid column headers to App.css.
//endregion Version 1.0.6.0
//region Version 1.0.7.0
//    024   13.06.25 Sean Flook       IMANN-1812 Moved controls from @mui/system to @mui/material.
//    025   28.08.25 Sean Flook       IMANN-1812 Changes required to update MUI to v7.
//    026   28.08.25 Sean Flook       IMANN-1812 Changes required to update MUI X to v8.
//    027   27.10.25 Sean Flook       IMANN-2110 Set getRowHeight to Auto in the grid so that text wraps correctly.
//    028   28.10.25 Sean Flook       IMANN-2110 Use formatTableStartDate and formatTableEndDate for date columns.
//    029   17.12.25 Sean Flook       IMANN-2196 Removed the information and selection components as no longer required in this control.
//endregion Version 1.0.7.0
//region Version 1.0.7.2
//    030   01.05.26 Sean Flook       IMANN-2430 Use currentClassificationSelection when using the list of ids currently selected when deleting.
//endregion Version 1.0.7.2
//
//--------------------------------------------------------------------------------------------------
//endregion header

import React, { useState, useRef, useContext, useEffect, Fragment } from "react";
import PropTypes from "prop-types";

import UserContext from "../context/userContext";
import SelectionContext from "../context/selectionContext";

import { formatTableEndDate, formatTableStartDate, GetOSClassificationAvatarAndText } from "../utils/HelperUtils";

import {
  Tooltip,
  IconButton,
  Typography,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Skeleton,
  Box,
  Stack,
} from "@mui/material";

import ADSActionButton from "../components/ADSActionButton";

import ConfirmDeleteDialog from "../dialogs/ConfirmDeleteDialog";

import { AddCircleOutlineOutlined as AddCircleIcon } from "@mui/icons-material";

import { adsBlueA, adsLightBlue10 } from "../utils/ADSColours";
import { toolbarStyle, ActionIconStyle, dataFormStyle, tooltipStyle, StyledDataGrid } from "../utils/ADSStyles";
import { useTheme } from "@mui/material/styles";

PropertyClassificationListTab.propTypes = {
  data: PropTypes.array,
  errors: PropTypes.array,
  loading: PropTypes.bool.isRequired,
  onClassificationSelected: PropTypes.func.isRequired,
  onClassificationDelete: PropTypes.func.isRequired,
  onMultiClassificationDelete: PropTypes.func.isRequired,
};

function PropertyClassificationListTab({
  data,
  errors,
  loading,
  onClassificationSelected,
  onClassificationDelete,
  onMultiClassificationDelete,
}) {
  const theme = useTheme();

  const userContext = useContext(UserContext);
  const selectionContext = useContext(SelectionContext);

  const [sortModel, setSortModel] = useState([{ field: "blpuClass", sort: "asc" }]);
  const [selectionModel, setSelectionModel] = useState({
    type: "include",
    ids: new Set([]),
  });
  const [selectedRow, setSelectedRow] = useState(null);

  const [userCanEdit, setUserCanEdit] = useState(false);

  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);

  const deleteClassificationId = useRef(null);

  /**
   * Method to display the action buttons for the given row.
   *
   * @param {object} params The grid row parameters.
   * @returns {JSX.Element} The action buttons for the row
   */
  const displayActionButtons = (params) => {
    const onDeleteClick = (event) => {
      event.stopPropagation();
      deleteClassificationId.current = params.id;
      setOpenDeleteConfirmation(true);
    };

    return (
      selectedRow &&
      params.id === selectedRow &&
      selectionModel &&
      selectionModel.ids.size < 2 && (
        <Stack direction="row" spacing={1} justifyContent="flex-start" alignItems="center">
          <ADSActionButton
            id={`delete-classification-${params.id}`}
            variant="delete"
            inheritBackground
            disabled={!userCanEdit}
            tooltipTitle="Delete classification"
            tooltipPlacement="bottom"
            onClick={onDeleteClick}
          />
        </Stack>
      )
    );
  };

  /**
   * Array of fields (columns) to be displayed in the data grid.
   */
  const classificationColumns = [
    { field: "id" },
    { field: "uprn" },
    { field: "changeType" },
    { field: "classKey" },
    {
      field: "blpuClass",
      headerName: "Classification",
      display: "flex",
      flex: 30,
      renderCell: GetOSClassificationAvatarAndText,
    },
    { field: "classificationScheme" },
    { field: "entryDate" },
    {
      field: "startDate",
      headerName: "Start",
      flex: 10,
      type: "date",
      align: "right",
      headerAlign: "right",
      valueGetter: (value, row, column, apiRef) => new Date(value),
      renderCell: formatTableStartDate,
    },
    {
      field: "endDate",
      headerName: "End",
      flex: 10,
      type: "date",
      align: "right",
      headerAlign: "right",
      valueGetter: (value, row, column, apiRef) => new Date(value),
      renderCell: formatTableEndDate,
    },
    { field: "lastUpdateDate" },
    {
      field: "",
      display: "flex",
      flex: 2,
      sortable: false,
      renderCell: displayActionButtons,
    },
  ];

  /**
   * Event to handle when the mouse enters a row in the data grid.
   *
   * @param {object} event The event object.
   */
  const handleRowMouseEnter = (event) => {
    event.preventDefault();

    setSelectedRow(Number(event.currentTarget.getAttribute("data-id")));
  };

  /**
   * Event to handle when the mouse leaves a row in the data grid.
   */
  const handleRowMouseLeave = () => {
    setSelectedRow(null);
  };

  /**
   * Event to handle when a classification record is clicked.
   *
   * @param {object} param The grid row parameters.
   * @param {object} event The event object.
   */
  const handleClassificationClicked = (param, event) => {
    event.stopPropagation();

    if (param && param.field !== "__check__" && param.field !== "") {
      let record = -1;
      for (let i = 0; i < data.length; i++) {
        if (data[i].id === param.id) {
          record = i;
          break;
        }
      }
      if (onClassificationSelected) onClassificationSelected(param.id, param.row, record, data.length);
    }
  };

  /**
   * Event to handle when the add classification button is clicked.
   *
   * @param {object} event The event object.
   */
  const handleAddClassificationClick = (event) => {
    event.stopPropagation();
    if (onClassificationSelected) onClassificationSelected(0, null, null, null);
  };

  /**
   * Event to handle when the selection model for the data grid changes.
   *
   * @param {object} selectionModel The current selection model for the data grid.
   */
  const handleSelectionModelChange = (selectionModel) => {
    if (selectionModel.type === "include" && selectionModel.ids.size > 0 && userCanEdit) {
      selectionContext.onSelectionVariantChange("classification");
      selectionContext.onSelectionCountChange(selectionModel.ids.size);
      selectionContext.onCurrentClassificationChange([...selectionModel.ids]);
    } else if (selectionModel.type === "exclude" && selectionModel.ids.size === 0 && userCanEdit) {
      selectionContext.onSelectionVariantChange("classification");
      selectionContext.onSelectionCountChange(data.length);
      const allIds = data.map((x) => x.id);
      selectionContext.onCurrentClassificationChange(allIds);
    } else if (selectionModel.type === "exclude" && selectionModel.ids.size < data.length && userCanEdit) {
      selectionContext.onSelectionVariantChange("classification");
      selectionContext.onSelectionCountChange(data.length - selectionModel.ids.size);
      const allIds = data.filter((x) => !selectionModel.ids.has(x.id)).map((x) => x.id);
      selectionContext.onCurrentClassificationChange(allIds);
    } else {
      selectionContext.onCloseSelection();
    }
  };

  /**
   * Event to handle when the delete confirmation dialog is closed.
   *
   * @param {boolean} deleteConfirmed True if the user has confirmed the delete; otherwise false.
   */
  const handleCloseDeleteConfirmation = (deleteConfirmed) => {
    setOpenDeleteConfirmation(false);
    if (deleteConfirmed) {
      if (selectionContext.currentClassificationSelection?.length > 0 && onMultiClassificationDelete) {
        onMultiClassificationDelete(selectionContext.currentClassificationSelection);
        selectionContext.onCloseSelection();
        setSelectionModel({
          type: "include",
          ids: new Set([]),
        });
      } else if (deleteClassificationId.current && onClassificationDelete) {
        onClassificationDelete(deleteClassificationId.current);
        deleteClassificationId.current = null;
      }
    }
  };

  /**
   * Method to determine if the row is valid or not.
   *
   * @param {number} id The id of the row.
   * @returns {boolean} True if the row is valid; otherwise false.
   */
  const isRowInvalid = (id) => {
    if (errors && errors.length > 0) {
      const rowErrors = errors.filter((x) => x.id === id);
      return rowErrors && rowErrors.length > 0;
    } else return false;
  };

  useEffect(() => {
    if (userCanEdit !== (userContext.currentUser && userContext.currentUser.editProperty))
      setUserCanEdit(userContext.currentUser && userContext.currentUser.editProperty);
  }, [userCanEdit, userContext.currentUser]);

  useEffect(() => {
    if (selectionContext.closeControl) {
      selectionContext.onControlClosed();
      setSelectionModel({
        type: "include",
        ids: new Set([]),
      });
    }
  }, [selectionContext, selectionContext.closeControl]);

  useEffect(() => {
    if (selectionContext.deleteClassification && selectionContext.currentClassificationSelection?.length > 0) {
      selectionContext.onDeleteClassification(false);
      setOpenDeleteConfirmation(true);
    }
  }, [selectionContext]);

  return (
    <Fragment>
      <Box sx={toolbarStyle} id="ads-property-classification-list-tab">
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle1" sx={{ pl: theme.spacing(2) }}>
            Classifications
          </Typography>
          <Tooltip title="Add new classification record" arrow placement="right" sx={tooltipStyle}>
            <IconButton
              sx={ActionIconStyle()}
              disabled={!userCanEdit}
              onClick={handleAddClassificationClick}
              size="small"
            >
              <AddCircleIcon />
              <Typography
                variant="subtitle1"
                sx={{
                  pl: theme.spacing(1),
                  pr: theme.spacing(1),
                }}
              >
                Classification
              </Typography>
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
      <Box
        sx={{
          ...dataFormStyle("PropertyClassificationListTab"),
        }}
      >
        {loading ? (
          <Skeleton variant="rectangular" height="30px" width="100%" />
        ) : data && data.filter((x) => x.changeType !== "D").length > 0 ? (
          <StyledDataGrid
            rows={data.filter((x) => x.changeType !== "D")}
            columns={classificationColumns}
            initialState={{
              columns: {
                columnVisibilityModel: {
                  id: false,
                  uprn: false,
                  changeType: false,
                  classKey: false,
                  classificationScheme: false,
                  entryDate: false,
                  lastUpdateDate: false,
                },
              },
            }}
            autoPageSize
            checkboxSelection
            disableColumnMenu
            pagination
            sortModel={sortModel}
            rowSelectionModel={selectionModel}
            slotProps={{
              row: {
                onMouseEnter: handleRowMouseEnter,
                onMouseLeave: handleRowMouseLeave,
              },
            }}
            getRowClassName={(params) => `${isRowInvalid(params.id) ? "invalid-row" : "valid-row"}`}
            getRowHeight={(params) => "auto"}
            onCellClick={handleClassificationClicked}
            onRowSelectionModelChange={(newSelectionModel) => {
              setSelectionModel(newSelectionModel);
              handleSelectionModelChange(newSelectionModel);
            }}
            onSortModelChange={(model) => setSortModel(model)}
          />
        ) : (
          <List
            sx={{
              width: "100%",
              backgroundColor: theme.palette.background.paper,
              pt: theme.spacing(0),
            }}
            component="nav"
            key="key_no_records"
          >
            <ListItemButton
              dense
              disabled={!userCanEdit}
              disableGutters
              onClick={handleAddClassificationClick}
              sx={{
                height: "30px",
                "&:hover": {
                  backgroundColor: adsLightBlue10,
                  color: adsBlueA,
                },
              }}
            >
              <ListItemText
                primary={
                  <Typography variant="subtitle1" sx={{ pl: "16px" }}>
                    No classification records present
                  </Typography>
                }
              />
              <ListItemAvatar
                sx={{
                  minWidth: 32,
                }}
              >
                <Tooltip title="Add classification record" arrow placement="bottom" sx={tooltipStyle}>
                  <IconButton disabled={!userCanEdit} onClick={handleAddClassificationClick} size="small">
                    <AddCircleIcon sx={ActionIconStyle()} />
                  </IconButton>
                </Tooltip>
              </ListItemAvatar>
            </ListItemButton>
          </List>
        )}
      </Box>
      <div>
        <ConfirmDeleteDialog
          variant="classification"
          recordCount={selectionModel && selectionModel.ids.size > 0 ? selectionModel.ids.size : 0}
          open={openDeleteConfirmation}
          onClose={handleCloseDeleteConfirmation}
        />
      </div>
    </Fragment>
  );
}

export default PropertyClassificationListTab;
